/* =========================
    IMPORTS & CORE PACKAGES
   ========================= */
import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import Sidebar from "./Sidebar";
import AdminPortal from "./AdminPortal";
import InstructorPortal from "./InstructorPortal";
import StudentPortal from "./StudentPortal";
import { useNavigate } from "react-router-dom";
import { Menu, X, Bell, User, Check, AlertTriangle } from "lucide-react";
import { db } from "../firebase";
import { collection, doc, updateDoc, query, orderBy, limit, onSnapshot } from "firebase/firestore";

export default function Dashboard() {
  const { userData, logout } = useAuth();
  const navigate = useNavigate();
  
  /* ================================
      COMPONENT DEFINITION & STATES
     ================================ */

  /* Navigation & Layout Controls */
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("");
  
  /* Notifications & Badge State */
  const [notifications, setNotifications] = useState([]);
  const [showNotifMenu, setShowNotifMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedNotif, setSelectedNotif] = useState(null); 

  // Holds the target sub-tab when we jump from a notification click
  const [initialSubTab, setInitialSubTab] = useState("");

  /* DOM References */
  const notifMenuRef = useRef(null);
  const detailsModalRef = useRef(null);

  /* =================================
      SYSTEM SYNC & LIFECYCLE EFFECTS
     ================================= */

  // Set default homepage views depending on the logged-in user role
  useEffect(() => {
    if (userData) {
      if (userData.role === "admin") {
        setActiveTab("approvals");
      } else if (userData.role === "instructor") {
        setActiveTab("dashboard");
      } else {
        setActiveTab("dashboard");
      }
    }
  }, [userData]);

  // Synchronizes the unread notifications count in real-time as soon as announcements arrive
  useEffect(() => {
    if (!userData) return;

    let unsubscribe = () => {};

    if (userData.role === "admin") {
      const q = query(collection(db, "auditLogs"), orderBy("timestamp", "desc"));
      unsubscribe = onSnapshot(q, (snap) => {
        const persistentReads = userData.readAudits || [];
        const unreadAudits = snap.docs.map(d => d.id).filter(id => !persistentReads.includes(id)).length;
        setUnreadCount(unreadAudits);
      });
    } else {
      const q = query(collection(db, "announcements"), orderBy("timestamp", "desc"), limit(10));
      unsubscribe = onSnapshot(q, (snap) => {
        const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        let filteredList = [];

        if (userData.role === "student") {
          const enrollments = userData.enrollments || [];
          const myEnrolledCourseCodes = enrollments.map(en => en.courseCode);
          
          filteredList = list.filter(n => {
            if (n.targetType === "personal_student") {
              return n.targetStudentUid === userData.uid; // Only show if UID matches
            }
            if (n.targetType === "all" || n.targetRole === "student") return true;
            if (n.targetType === "course" || n.targetType === "students_course") {
              return myEnrolledCourseCodes.includes(n.targetCourseId);
            }
            if (n.targetType === "batch") {
              return enrollments.some(en => en.courseCode === n.targetCourseId && en.batchName === n.targetBatchId);
            }
            return false;
          });
        } else if (userData.role === "instructor") {
          filteredList = list.filter(n => {
            if (n.targetType === "all" || n.targetRole === "instructor") return true;
            if (n.targetType === "course" || n.targetType === "instructors_course") {
              return (userData.assignedCourses || []).includes(n.targetCourseId);
            }
            if (n.targetType === "batch") {
              return (userData.assignedAllocations || []).some(al => al.courseCode === n.targetCourseId && al.batchName === n.targetBatchId);
            }
            return false;
          });
        }

        setNotifications(filteredList);
        const persistentReads = userData.readNotifications || [];
        setUnreadCount(filteredList.filter(n => !persistentReads.includes(n.id)).length);
      });
    }

    return () => unsubscribe();
  }, [userData]);

  // Closes menus if the user clicks anywhere outside of them
  useEffect(() => {
    function handleClickOutside(event) {
      if (notifMenuRef.current && !notifMenuRef.current.contains(event.target)) {
        setShowNotifMenu(false);
      }
      if (detailsModalRef.current && !detailsModalRef.current.contains(event.target)) {
        setSelectedNotif(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ==========================================
      DATABASE & NOTIFICATIONS ACTION HANDLERS
     ========================================== */

  // Saves read notification IDs in Firestore so they don't show up as unread again
  async function togglePersistentRead(id, action) {
    if (!userData) return;
    const persistentReads = userData.readNotifications || [];
    let updatedReads = [...persistentReads];

    if (action === "read" && !persistentReads.includes(id)) {
      updatedReads.push(id);
    } else if (action === "unread" && persistentReads.includes(id)) {
      updatedReads = updatedReads.filter(rid => rid !== id);
    }

    await updateDoc(doc(db, "users", userData.uid), {
      readNotifications: updatedReads
    });

    setUnreadCount(notifications.filter(n => !updatedReads.includes(n.id)).length);
  }

  /* Sign Out Security Handler */
  async function handleLogout() {
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      console.error("Logout error: ", err);
    }
  }

  const persistentReads = userData?.readNotifications || [];
  const isAdmin = userData?.role === "admin";
  const isApproved = userData?.status === "approved" || userData?.role === "admin";

  /* ==================================
      UNAPPROVED ACCESS SECURITY GUARD 
     ================================== */
  // If the student or instructor is not approved yet, block all page items and show warning
  if (userData && !isApproved) {
    return (
      <div 
        style={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          minHeight: "100vh", 
          backgroundColor: "var(--bg-slate)", 
          padding: "1.5rem" 
        }}
      >
        <div 
          className="card" 
          style={{ 
            maxWidth: "500px", 
            width: "100%", 
            textAlign: "center", 
            padding: "3rem 2rem", 
            borderTop: "4px solid var(--warning)",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }}
        >
          <div 
            style={{ 
              display: "inline-flex", 
              background: "var(--warning)", 
              padding: "1rem", 
              borderRadius: "50%", 
              marginBottom: "1.5rem", 
              color: "white" 
            }}
          >
            <AlertTriangle size={40} />
          </div>
          <h2 style={{ color: "var(--navy-dark)", fontSize: "1.5rem", fontWeight: "800", marginBottom: "1rem" }}>
            Awaiting Approval
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: "0.95rem", lineHeight: "1.6", marginBottom: "2rem" }}>
            Hello <strong>{userData.fullName}</strong>. Your registration is currently pending administrative approval. 
            You will be granted access to the Oncode APT System as an <strong>{userData.role}</strong> once an administrator approves your account.
          </p>
          <button 
            className="navy-btn navy-btn-danger" 
            onClick={handleLogout} 
            style={{ width: "100%", justifyContent: "center", padding: "1rem" }}
          >
            Sign Out of System
          </button>
        </div>
      </div>
    );
  }

  /* ==============================================
      DASHBOARD VIEWPORT RENDERER (APPROVED USERS)
     ============================================== */
  return (
    <div className="app-container">
      
      {/* Detailed Announcement Popup Modal */}
      {selectedNotif && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
          <div className="card" ref={detailsModalRef} style={{ maxWidth: "500px", width: "100%", padding: "2rem", position: "relative" }}>
            <button onClick={() => setSelectedNotif(null)} style={{ position: "absolute", top: "1rem", right: "1rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}><X size={20} /></button>
            <h3 style={{ color: "var(--navy-primary)", marginBottom: "1rem" }}>{selectedNotif.title}</h3>
            <p style={{ color: "var(--text-main)", marginBottom: "1.5rem", lineHeight: "1.6" }}>{selectedNotif.content}</p>
            <small style={{ color: "var(--text-muted)", display: "block", marginBottom: "1.5rem" }}>Date: {new Date(selectedNotif.timestamp).toLocaleString()}</small>
            
            <div style={{ display: "flex", gap: "1rem" }}>
              {!persistentReads.includes(selectedNotif.id) ? (
                <button className="navy-btn" onClick={() => { togglePersistentRead(selectedNotif.id, "read"); setSelectedNotif(null); }} style={{ background: "var(--success)" }}><Check size={16}/> Mark as Read</button>
              ) : (
                <button className="navy-btn" onClick={() => { togglePersistentRead(selectedNotif.id, "unread"); setSelectedNotif(null); }} style={{ background: "var(--warning)" }}>Mark as Unread</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* -Mobile Header Navigation */}
      <header className="mobile-header">
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <img src="/logo192.png" alt="Logo" style={{ width: "70px", height: "auto" }} />
          <span style={{ fontWeight: "800", fontSize: "1.1rem" }}>Oncode APT System</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }} ref={notifMenuRef}>
          {!isAdmin && (
            <div style={{ position: "relative" }}>
              <button 
                onClick={() => { if (!isAdmin) setShowNotifMenu(!showNotifMenu); }} 
                style={{ background: "none", border: "none", color: "white", cursor: "pointer" }}
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span style={{ position: "absolute", top: "-5px", right: "-5px", background: "var(--danger)", color: "white", borderRadius: "50%", fontSize: "0.65rem", padding: "2px 5px" }}>
                    {unreadCount}
                  </span>
                )}
              </button>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: "none", border: "none", color: "white", cursor: "pointer" }}>
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Main Sidebar Component Wrapper */}
      <Sidebar 
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        role={userData?.role || "student"}
        userData={userData}
        handleLogout={handleLogout}
      />

      {/* Main Desktop View Content Area */}
      <main className="main-content">
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1.5rem", position: "relative" }} className="desktop-header-bar" ref={notifMenuRef}>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            
            {/* Hide notification bell on desktop if logged in as Admin */}
            {!isAdmin && (
              <div style={{ position: "relative" }}>
                <button 
                  onClick={() => { if (!isAdmin) setShowNotifMenu(!showNotifMenu); }} 
                  style={{ background: "none", border: "none", color: "var(--navy-primary)", cursor: "pointer", padding: "0.5rem", position: "relative" }}
                >
                  <Bell size={22} />
                  {unreadCount > 0 && (
                    <span style={{ position: "absolute", top: "0px", right: "0px", background: "var(--danger)", color: "white", borderRadius: "50%", fontSize: "0.65rem", padding: "2px 5px" }}>
                      {unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Overlay Menu Dropdown */}
                {showNotifMenu && (
                  <div className="card" style={{ position: "absolute", right: "0", top: "40px", width: "320px", zIndex: "102", padding: "1rem", boxShadow: "0 10px 15px rgba(0,0,0,0.1)", maxHeight: "400px", overflowY: "auto" }}>
                    <h4 style={{ marginBottom: "0.75rem", borderBottom: "1px solid var(--border-color)", paddingBottom: "0.5rem" }}>Dispatched Notifications</h4>
                    {notifications.length === 0 ? (
                      <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>No new notifications.</p>
                    ) : (
                      <ul style={{ listStyle: "none", padding: "0" }}>
                        {notifications.map(n => {
                          const isRead = persistentReads.includes(n.id);
                          return (
                            <li 
                              key={n.id} 
                              onClick={() => {
                                // Mark as read instantly in database
                                togglePersistentRead(n.id, "read");
                                
                                // Setup the target sub-tab when we jump from notification clicks
                                if (userData?.role === "instructor") {
                                  setInitialSubTab("received"); // Jump to Received Announcements for Instructors
                                } else if (userData?.role === "student") {
                                  setInitialSubTab("new");      // Jump to New Announcements for Students
                                }
                                
                                // Auto-navigate to Announcements Page dynamically
                                setActiveTab("announcements");
                                
                                // Hide notifications menu dropdown
                                setShowNotifMenu(false);
                              }}
                              style={{ 
                                marginBottom: "0.75rem", 
                                fontSize: "0.85rem", 
                                borderBottom: "1.2px solid #f1f5f9", 
                                paddingBottom: "0.5rem",
                                cursor: "pointer",
                                opacity: isRead ? "0.6" : "1",
                                borderLeft: isRead ? "none" : "3px solid var(--navy-light)",
                                paddingLeft: isRead ? "0" : "0.5rem",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                transition: "all 0.2s ease"
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <strong>{n.title}</strong>
                                <p style={{ color: "var(--text-muted)", margin: "0.15rem 0", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", maxWidth: "240px" }}>{n.content}</p>
                                <small style={{ color: "var(--navy-light)" }}>{new Date(n.timestamp).toLocaleDateString()}</small>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <User size={18} />
              <span>Logged as <strong>{userData?.role?.toUpperCase()}</strong></span>
            </div>
          </div>
        </div>

        {/* Dynamic Portal Views Routing */}
        {userData?.role === "admin" && <AdminPortal activeTab={activeTab} />}
        
        {userData?.role === "instructor" && (
          <InstructorPortal 
            activeTab={activeTab} 
            initialSubTab={initialSubTab} 
            setInitialSubTab={setInitialSubTab} 
          />
        )}
        
        {userData?.role === "student" && (
          <StudentPortal 
            activeTab={activeTab} 
            initialSubTab={initialSubTab} 
            setInitialSubTab={setInitialSubTab} 
          />
        )}
      </main>
    </div>
  );
}